import type { LifeDayInput, ObligationInput, SparkEntryInput } from "@moneymap/contracts";
import {
  Button,
  Callout,
  type CalmStatus,
  Card,
  EvidenceList,
  FormField,
  Modal,
  PageHeader,
  ProgressMeter,
  SectionHeader,
  SelectInput,
  StatePanel,
  StatusBadge,
  TextInput,
} from "@moneymap/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useApi } from "../api/ApiProvider";
import type { AccountRead, ObligationRead, PurposeRead } from "../api/types";
import { QueryState } from "../components/QueryState";
import {
  formatHouseholdDate,
  formatMoney,
  parseMoneyInput,
  titleFromMachineValue,
} from "../lib/format";

function planStatus(value: string): CalmStatus {
  if (
    value === "covered" ||
    value === "partially_covered" ||
    value === "at_risk" ||
    value === "paid"
  )
    return value;
  return "needs_confirmation";
}

type ObligationFormValues = {
  name: string;
  payee: string;
  type: ObligationInput["type"];
  frequency: ObligationInput["recurrence"]["frequency"];
  anchorDate: string;
  expectedAmount: string;
  purposeId: string;
  priority: string;
  flexibility: ObligationInput["flexibility"];
  sourceAccountId: string;
  splitAllowed: boolean;
};

function ObligationFormModal({
  isOpen,
  onClose,
  purposes,
  accounts,
  currency,
}: {
  isOpen: boolean;
  onClose: () => void;
  purposes: PurposeRead[];
  accounts: AccountRead[];
  currency: string;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<string>();
  const { register, handleSubmit, reset } = useForm<ObligationFormValues>({
    defaultValues: {
      name: "",
      payee: "",
      type: "bill",
      frequency: "monthly",
      anchorDate: new Date().toISOString().slice(0, 10),
      expectedAmount: "",
      purposeId: "",
      priority: "50",
      flexibility: "fixed",
      sourceAccountId: "",
      splitAllowed: true,
    },
  });
  const mutation = useMutation({
    mutationFn: (input: ObligationInput) => api.createObligation(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["plan"] });
      await queryClient.invalidateQueries({ queryKey: ["briefing"] });
      reset();
      onClose();
    },
  });
  const submit = handleSubmit((values) => {
    setFormError(undefined);
    const expectedAmountMinor = parseMoneyInput(values.expectedAmount);
    if (!expectedAmountMinor || expectedAmountMinor.startsWith("-") || !values.purposeId) {
      setFormError("Enter a positive expected amount and choose a purpose.");
      return;
    }
    mutation.mutate({
      name: values.name,
      ...(values.payee ? { payee: values.payee } : {}),
      type: values.type,
      recurrence: { frequency: values.frequency, interval: 1, anchorDate: values.anchorDate },
      amountMethod: "user_confirmed",
      expectedAmountMinor,
      purposeId: values.purposeId,
      priority: Number(values.priority),
      flexibility: values.flexibility,
      ...(values.sourceAccountId ? { sourceAccountId: values.sourceAccountId } : {}),
      splitAllowed: values.splitAllowed,
      confidence: "confirmed",
    });
  });

  return (
    <Modal
      isOpen={isOpen}
      title="Add an obligation"
      description="Known obligations reserve money before anything is called safe to use."
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="obligation-form" isLoading={mutation.isPending}>
            Save obligation
          </Button>
        </>
      }
    >
      <form id="obligation-form" className="form-stack" onSubmit={submit}>
        <div className="form-grid form-grid--two">
          <FormField label="Name" htmlFor="plan-obligation-name">
            <TextInput id="plan-obligation-name" {...register("name", { required: true })} />
          </FormField>
          <FormField label="Payee" htmlFor="plan-obligation-payee">
            <TextInput id="plan-obligation-payee" {...register("payee")} />
          </FormField>
          <FormField label="Type" htmlFor="plan-obligation-type">
            <SelectInput id="plan-obligation-type" {...register("type")}>
              <option value="bill">Bill</option>
              <option value="debt_minimum">Debt minimum</option>
              <option value="groceries">Groceries</option>
              <option value="hockey_school">Hockey / school</option>
              <option value="subscription">Subscription</option>
              <option value="other">Other</option>
            </SelectInput>
          </FormField>
          <FormField label="Frequency" htmlFor="plan-obligation-frequency">
            <SelectInput id="plan-obligation-frequency" {...register("frequency")}>
              <option value="once">Once</option>
              <option value="weekly">Weekly</option>
              <option value="biweekly">Every two weeks</option>
              <option value="monthly">Monthly</option>
              <option value="yearly">Yearly</option>
            </SelectInput>
          </FormField>
          <FormField label="Next due date" htmlFor="plan-obligation-date">
            <TextInput
              id="plan-obligation-date"
              type="date"
              {...register("anchorDate", { required: true })}
            />
          </FormField>
          <FormField label={"Expected amount (" + currency + ")"} htmlFor="plan-obligation-amount">
            <TextInput
              id="plan-obligation-amount"
              inputMode="decimal"
              placeholder="0.00"
              {...register("expectedAmount", { required: true })}
            />
          </FormField>
          <FormField label="Purpose" htmlFor="plan-obligation-purpose">
            <SelectInput id="plan-obligation-purpose" {...register("purposeId")}>
              <option value="">Choose a purpose</option>
              {purposes.map((purpose) => (
                <option value={purpose.id} key={purpose.id}>
                  {purpose.name}
                </option>
              ))}
            </SelectInput>
          </FormField>
          <FormField label="Payment account" htmlFor="plan-obligation-account">
            <SelectInput id="plan-obligation-account" {...register("sourceAccountId")}>
              <option value="">Choose later</option>
              {accounts.map((account) => (
                <option value={account.id} key={account.id}>
                  {account.name}
                </option>
              ))}
            </SelectInput>
          </FormField>
          <FormField
            label="Priority"
            htmlFor="plan-obligation-priority"
            hint="1 is highest; 100 is lowest."
          >
            <TextInput
              id="plan-obligation-priority"
              type="number"
              min={1}
              max={100}
              {...register("priority")}
            />
          </FormField>
          <FormField label="Timing flexibility" htmlFor="plan-obligation-flexibility">
            <SelectInput id="plan-obligation-flexibility" {...register("flexibility")}>
              <option value="fixed">Fixed</option>
              <option value="flexible">Flexible</option>
              <option value="pay_when_funded">Pay when funded</option>
            </SelectInput>
          </FormField>
        </div>
        <label className="check-row">
          <input type="checkbox" {...register("splitAllowed")} />
          <span>Allow funding across multiple income events.</span>
        </label>
        {formError ? (
          <Callout title="Check this obligation" tone="warning">
            {formError}
          </Callout>
        ) : null}
        {mutation.error ? (
          <Callout title="Obligation not saved" tone="urgent">
            {mutation.error.message}
          </Callout>
        ) : null}
      </form>
    </Modal>
  );
}

export function PlanPage() {
  const api = useApi();
  const [isObligationOpen, setIsObligationOpen] = useState(false);
  const planQuery = useQuery({ queryKey: ["plan"], queryFn: () => api.getPlan() });
  const accountsQuery = useQuery({ queryKey: ["accounts"], queryFn: () => api.getAccounts() });
  const purposesQuery = useQuery({ queryKey: ["purposes"], queryFn: () => api.getPurposes() });
  const plan = planQuery.data?.data;
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Continuous cash flow"
        title="Plan the next money windows"
        description="IterNest Money follows paychecks and obligations across calendar months; it never resets the household story on the first."
        actions={
          <Button type="button" onClick={() => setIsObligationOpen(true)}>
            Add obligation
          </Button>
        }
      />
      <QueryState
        isPending={planQuery.isPending}
        isError={planQuery.isError}
        error={planQuery.error}
        onRetry={() => void planQuery.refetch()}
      >
        {plan ? (
          <>
            <Card>
              <SectionHeader
                title="Paycheck windows"
                description={
                  formatHouseholdDate(plan.fromDate) +
                  " through " +
                  formatHouseholdDate(plan.toDate)
                }
              />
              {plan.paycheckWindows.length === 0 ? (
                <StatePanel title="No paycheck windows yet">
                  Add a confirmed income source and at least one obligation to build this timeline.
                </StatePanel>
              ) : (
                <div className="timeline">
                  {plan.paycheckWindows.map((window) => (
                    <article className="timeline-card" key={window.id}>
                      <div className="timeline-card__header">
                        <div>
                          <strong>{window.label}</strong>
                          <span>
                            {formatHouseholdDate(window.startDate)} –{" "}
                            {formatHouseholdDate(window.endDate)}
                          </span>
                        </div>
                        <StatusBadge status={planStatus(window.status)} />
                      </div>
                      {window.incomeLabel ? (
                        <p className="small muted">
                          {window.incomeLabel}
                          {window.expectedIncomeMinor
                            ? " · " + formatMoney(window.expectedIncomeMinor, plan.currency)
                            : ""}
                          {window.incomeConfidence
                            ? " · " + titleFromMachineValue(window.incomeConfidence)
                            : ""}
                        </p>
                      ) : null}
                      <div className="card-list">
                        {window.jobs.map((job) => (
                          <div className="list-row" key={job.id}>
                            <div className="list-row__main">
                              <strong>{job.label}</strong>
                              <span>
                                {job.dueDate ? formatHouseholdDate(job.dueDate) : "Protected job"} ·{" "}
                                {titleFromMachineValue(job.state)}
                              </span>
                            </div>
                            <div className="list-row__side">
                              <strong>{formatMoney(job.fundedMinor, plan.currency)}</strong>
                              <span className="small muted">
                                of {formatMoney(job.plannedMinor, plan.currency)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </Card>
            <Card>
              <SectionHeader
                title="Upcoming obligations"
                description="Expected amount, funded-to-date, remaining amount, and the next planned contribution."
              />
              {plan.obligations.length === 0 ? (
                <StatePanel title="No obligations are active">
                  Add bills, debt minimums, groceries, hockey or school, and other commitments.
                </StatePanel>
              ) : (
                <div className="card-list">
                  {plan.obligations.map((obligation) => (
                    <Link
                      className="list-row"
                      to={"/obligations/" + obligation.id}
                      key={obligation.id}
                    >
                      <div className="list-row__main">
                        <strong>{obligation.name}</strong>
                        <span>
                          {obligation.nextDueDate
                            ? formatHouseholdDate(obligation.nextDueDate)
                            : "Date needs confirmation"}
                          {" · " + obligation.purposeName}
                        </span>
                      </div>
                      <div className="list-row__side">
                        <strong>
                          {formatMoney(obligation.remainingMinor, obligation.currency)} left
                        </strong>
                        <StatusBadge status={planStatus(obligation.fundingState)} />
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </Card>
          </>
        ) : null}
      </QueryState>
      <ObligationFormModal
        isOpen={isObligationOpen}
        onClose={() => setIsObligationOpen(false)}
        purposes={purposesQuery.data?.data ?? []}
        accounts={accountsQuery.data?.data ?? []}
        currency={plan?.currency ?? accountsQuery.data?.data[0]?.currency ?? "USD"}
      />
    </div>
  );
}

export function ObligationDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const api = useApi();
  const queryClient = useQueryClient();
  const [isFundingOpen, setIsFundingOpen] = useState(false);
  const [fundingAmount, setFundingAmount] = useState("");
  const [incomeEventId, setIncomeEventId] = useState("");
  const query = useQuery({ queryKey: ["obligation", id], queryFn: () => api.getObligation(id) });
  const incomeQuery = useQuery({
    queryKey: ["income-events"],
    queryFn: () => api.getIncomeEvents(),
  });
  const fundingMutation = useMutation({
    mutationFn: (obligation: ObligationRead) => {
      const plannedAmountMinor = parseMoneyInput(fundingAmount);
      if (!plannedAmountMinor || plannedAmountMinor.startsWith("-")) {
        throw new Error("Enter a positive planned contribution.");
      }
      return api.saveFundingPlan(obligation.occurrenceId ?? obligation.id, {
        incomeEventId,
        plannedAmountMinor,
      });
    },
    onSuccess: async () => {
      setIsFundingOpen(false);
      await queryClient.invalidateQueries({ queryKey: ["plan"] });
      await query.refetch();
    },
  });
  const obligation = query.data?.data;
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Obligation"
        title={obligation?.name ?? "Obligation detail"}
        actions={
          <Button type="button" onClick={() => setIsFundingOpen(true)} disabled={!obligation}>
            Plan funding
          </Button>
        }
      />
      <QueryState
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        onRetry={() => void query.refetch()}
      >
        {obligation ? (
          <>
            <Card>
              <div className="summary-grid">
                <div className="summary-tile">
                  <span>Expected</span>
                  <strong>
                    {formatMoney(obligation.expectedAmountMinor ?? "0", obligation.currency)}
                  </strong>
                </div>
                <div className="summary-tile">
                  <span>Funded</span>
                  <strong>{formatMoney(obligation.fundedMinor, obligation.currency)}</strong>
                </div>
                <div className="summary-tile">
                  <span>Remaining</span>
                  <strong>{formatMoney(obligation.remainingMinor, obligation.currency)}</strong>
                </div>
                <div className="summary-tile">
                  <span>Next due</span>
                  <strong>
                    {obligation.nextDueDate
                      ? formatHouseholdDate(obligation.nextDueDate)
                      : "Needs confirmation"}
                  </strong>
                </div>
                <div className="summary-tile">
                  <span>Purpose</span>
                  <strong>{obligation.purposeName}</strong>
                </div>
                <div className="summary-tile">
                  <span>Funding state</span>
                  <StatusBadge status={planStatus(obligation.fundingState)} />
                </div>
              </div>
            </Card>
            <Card>
              <SectionHeader title="Rules and source" />
              <dl className="detail-list">
                <div>
                  <dt>Amount method</dt>
                  <dd>{titleFromMachineValue(obligation.amountMethod)}</dd>
                </div>
                <div>
                  <dt>Timing</dt>
                  <dd>{titleFromMachineValue(obligation.flexibility)}</dd>
                </div>
                <div>
                  <dt>Payment account</dt>
                  <dd>{obligation.sourceAccountName ?? "Not assigned"}</dd>
                </div>
                <div>
                  <dt>Split funding</dt>
                  <dd>{obligation.splitAllowed ? "Allowed" : "One funding source"}</dd>
                </div>
                <div>
                  <dt>Priority</dt>
                  <dd>{obligation.priority}</dd>
                </div>
              </dl>
              <p className="small muted">
                A funding plan reserves intent. It is not proof that money moved or a payment was
                scheduled.
              </p>
            </Card>
          </>
        ) : null}
      </QueryState>
      <Modal
        isOpen={isFundingOpen}
        title="Plan the next contribution"
        description="Choose an expected income event and amount. The plan adapts when the actual deposit arrives."
        onClose={() => setIsFundingOpen(false)}
        footer={
          <>
            <Button type="button" variant="quiet" onClick={() => setIsFundingOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              isLoading={fundingMutation.isPending}
              disabled={!incomeEventId || !fundingAmount}
              onClick={() => {
                if (obligation) fundingMutation.mutate(obligation);
              }}
            >
              Save funding plan
            </Button>
          </>
        }
      >
        <div className="form-stack">
          <FormField label="Expected income event" htmlFor="funding-income">
            <SelectInput
              id="funding-income"
              value={incomeEventId}
              onChange={(event) => setIncomeEventId(event.currentTarget.value)}
            >
              <option value="">Choose income</option>
              {(incomeQuery.data?.data ?? [])
                .filter((event) => event.status === "expected" || event.status === "confirmed")
                .map((event) => (
                  <option value={event.id} key={event.id}>
                    {event.sourceName}
                    {event.expectedDate ? " · " + formatHouseholdDate(event.expectedDate) : ""}
                  </option>
                ))}
            </SelectInput>
          </FormField>
          <FormField
            label={"Planned amount (" + (obligation?.currency ?? "") + ")"}
            htmlFor="funding-amount"
          >
            <TextInput
              id="funding-amount"
              inputMode="decimal"
              value={fundingAmount}
              onChange={(event) => setFundingAmount(event.currentTarget.value)}
            />
          </FormField>
          {fundingMutation.error ? (
            <Callout title="Funding plan not saved" tone="urgent">
              {fundingMutation.error.message}
            </Callout>
          ) : null}
        </div>
      </Modal>
    </div>
  );
}

type SparkEntryForm = {
  workDate: string;
  confirmedEarnings: string;
  unconfirmedTips: string;
  hours: string;
  note: string;
};

function SparkEntryModal({
  isOpen,
  onClose,
  currency,
}: {
  isOpen: boolean;
  onClose: () => void;
  currency: string;
}) {
  const api = useApi();
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<string>();
  const { register, handleSubmit, reset } = useForm<SparkEntryForm>({
    defaultValues: {
      workDate: new Date().toISOString().slice(0, 10),
      confirmedEarnings: "",
      unconfirmedTips: "",
      hours: "",
      note: "",
    },
  });
  const mutation = useMutation({
    mutationFn: (input: SparkEntryInput) => api.createSparkEntry(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["spark"] });
      await queryClient.invalidateQueries({ queryKey: ["briefing"] });
      reset();
      onClose();
    },
  });
  const submit = handleSubmit((values) => {
    setFormError(undefined);
    const confirmedEarningsMinor = parseMoneyInput(values.confirmedEarnings || "0");
    const unconfirmedTipsMinor = parseMoneyInput(values.unconfirmedTips || "0");
    if (
      !confirmedEarningsMinor ||
      !unconfirmedTipsMinor ||
      confirmedEarningsMinor.startsWith("-") ||
      unconfirmedTipsMinor.startsWith("-")
    ) {
      setFormError("Earnings and tips must be zero or positive.");
      return;
    }
    const hoursValue = values.hours ? Number(values.hours) : undefined;
    mutation.mutate({
      workDate: values.workDate,
      confirmedEarningsMinor,
      unconfirmedTipsMinor,
      ...(hoursValue !== undefined && Number.isFinite(hoursValue)
        ? { hoursMinutes: Math.round(hoursValue * 60) }
        : {}),
      ...(values.note ? { note: values.note } : {}),
    });
  });
  return (
    <Modal
      isOpen={isOpen}
      title="Record today’s Spark"
      description="Confirmed earnings count toward progress. They do not become bank coverage until a deposit posts and reconciles."
      onClose={onClose}
      footer={
        <>
          <Button type="button" variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="spark-entry-form" isLoading={mutation.isPending}>
            Save Spark entry
          </Button>
        </>
      }
    >
      <form id="spark-entry-form" className="form-stack" onSubmit={submit}>
        <FormField label="Work date" htmlFor="spark-work-date">
          <TextInput
            id="spark-work-date"
            type="date"
            {...register("workDate", { required: true })}
          />
        </FormField>
        <div className="form-grid form-grid--two">
          <FormField label={"Confirmed earnings (" + currency + ")"} htmlFor="spark-earnings">
            <TextInput
              id="spark-earnings"
              inputMode="decimal"
              placeholder="0.00"
              {...register("confirmedEarnings")}
            />
          </FormField>
          <FormField label={"Unconfirmed tips (" + currency + ")"} htmlFor="spark-tips">
            <TextInput
              id="spark-tips"
              inputMode="decimal"
              placeholder="0.00"
              {...register("unconfirmedTips")}
            />
          </FormField>
          <FormField label="Hours worked" htmlFor="spark-hours">
            <TextInput
              id="spark-hours"
              type="number"
              min="0"
              max="24"
              step="0.25"
              {...register("hours")}
            />
          </FormField>
        </div>
        <FormField label="Optional note" htmlFor="spark-note">
          <textarea id="spark-note" className="mm-input" {...register("note")} />
        </FormField>
        {formError ? (
          <Callout title="Check the amounts" tone="warning">
            {formError}
          </Callout>
        ) : null}
        {mutation.error ? (
          <Callout title="Spark entry not saved" tone="urgent">
            {mutation.error.message}
          </Callout>
        ) : null}
      </form>
    </Modal>
  );
}

export function SparkPage() {
  const api = useApi();
  const [isEntryOpen, setIsEntryOpen] = useState(false);
  const query = useQuery({ queryKey: ["spark"], queryFn: () => api.getSpark() });
  const spark = query.data?.data;
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Variable-income plan"
        title="Spark"
        description="See the required goal first, then comfortable and aggressive options. A life day changes the plan without blame."
        actions={
          <div className="button-row">
            <Button type="button" onClick={() => setIsEntryOpen(true)}>
              Record earnings
            </Button>
            <Link className="mm-button mm-button--secondary" to="/life-days/new">
              Record life day
            </Link>
          </div>
        }
      />
      <QueryState
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        onRetry={() => void query.refetch()}
      >
        {spark ? (
          <>
            {spark.status === "done_required" ? (
              <Callout title="You are done for the required goal" tone="positive">
                Comfortable and aggressive progress remains optional.
              </Callout>
            ) : null}
            {spark.status === "planning_problem" ? (
              <Callout title="The current work plan needs an adjustment" tone="warning">
                There are no workable days left for the required amount. Review obligations, dates,
                or available days; IterNest Money will not show an impossible average.
              </Callout>
            ) : null}
            {spark.status === "not_configured" ? (
              <StatePanel
                title="Set up Spark rules"
                action={
                  <Link className="mm-button mm-button--secondary" to="/settings/rules">
                    Open rules
                  </Link>
                }
              >
                Add goal increments, usual workdays, and expected net per workday.
              </StatePanel>
            ) : (
              <>
                <Card className="spark-total-card">
                  <SectionHeader
                    title={spark.horizonLabel}
                    description="Confirmed work, required pressure, and optional stretch."
                  />
                  <div className="summary-grid">
                    <div className="summary-tile">
                      <span>Remaining required</span>
                      <strong>{formatMoney(spark.remainingTargetMinor, spark.currency)}</strong>
                    </div>
                    <div className="summary-tile">
                      <span>Workable days</span>
                      <strong>{spark.workableDays}</strong>
                    </div>
                    <div className="summary-tile">
                      <span>Remaining daily average</span>
                      <strong>
                        {spark.remainingDailyAverageMinor
                          ? formatMoney(spark.remainingDailyAverageMinor, spark.currency)
                          : "Planning needed"}
                      </strong>
                    </div>
                  </div>
                  <div className="section-stack">
                    <ProgressMeter
                      label="Minimum / Freedom Number"
                      value={spark.progress.minimumPercent}
                      helper={formatMoney(spark.minimumTargetMinor, spark.currency)}
                    />
                    <ProgressMeter
                      label="Comfortable"
                      value={spark.progress.comfortablePercent}
                      helper={formatMoney(spark.comfortableTargetMinor, spark.currency)}
                    />
                    <ProgressMeter
                      label="Aggressive"
                      value={spark.progress.aggressivePercent}
                      helper={formatMoney(spark.aggressiveTargetMinor, spark.currency)}
                    />
                  </div>
                </Card>
                <Card>
                  <SectionHeader title="Today and this horizon" />
                  <div className="summary-grid">
                    <div className="summary-tile">
                      <span>Confirmed earnings</span>
                      <strong>{formatMoney(spark.confirmedEarningsMinor, spark.currency)}</strong>
                    </div>
                    <div className="summary-tile">
                      <span>Unconfirmed tips</span>
                      <strong>{formatMoney(spark.unconfirmedTipsMinor, spark.currency)}</strong>
                    </div>
                    <div className="summary-tile">
                      <span>Estimated workdays needed</span>
                      <strong>{spark.estimatedDaysNeeded ?? "Not enough history"}</strong>
                    </div>
                  </div>
                </Card>
                <Card>
                  <SectionHeader
                    title="Next deposit jobs"
                    description="Provisional until the deposit posts; no money is counted twice."
                  />
                  {spark.nextDepositJobs.length === 0 ? (
                    <p className="muted">
                      No jobs are assigned to the next expected Spark deposit.
                    </p>
                  ) : (
                    <div className="card-list">
                      {spark.nextDepositJobs.map((job) => (
                        <div className="list-row" key={job.id}>
                          <div className="list-row__main">
                            <strong>{job.label}</strong>
                          </div>
                          <strong>{formatMoney(job.plannedMinor, spark.currency)}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
                <Card>
                  <SectionHeader
                    title="Spark history"
                    description="Entries, deposits, reconciliation, and life days."
                  />
                  {spark.entries.length === 0 && spark.lifeDays.length === 0 ? (
                    <StatePanel title="No Spark history yet">
                      Record confirmed work or a life day when the plan changes.
                    </StatePanel>
                  ) : (
                    <div className="card-list">
                      {spark.entries.map((entry) => (
                        <div className="list-row" key={entry.id}>
                          <div className="list-row__main">
                            <strong>{formatHouseholdDate(entry.workDate)}</strong>
                            <span>
                              {titleFromMachineValue(entry.reconciliationStatus)}
                              {entry.note ? " · " + entry.note : ""}
                            </span>
                          </div>
                          <div className="list-row__side">
                            <strong>
                              {formatMoney(entry.confirmedEarningsMinor, spark.currency)}
                            </strong>
                            {entry.unconfirmedTipsMinor !== "0" ? (
                              <span className="small muted">
                                {formatMoney(entry.unconfirmedTipsMinor, spark.currency)} tips
                                pending
                              </span>
                            ) : null}
                          </div>
                        </div>
                      ))}
                      {spark.lifeDays.map((lifeDay) => (
                        <div className="list-row" key={lifeDay.id}>
                          <div className="list-row__main">
                            <strong>{formatHouseholdDate(lifeDay.lifeDate)} · Life day</strong>
                            <span>
                              {titleFromMachineValue(lifeDay.type)}
                              {lifeDay.note ? " · " + lifeDay.note : ""}
                            </span>
                          </div>
                          <StatusBadge status="plan_updated" />
                        </div>
                      ))}
                    </div>
                  )}
                  <EvidenceList
                    items={spark.evidence}
                    title="How this Spark target was calculated"
                  />
                </Card>
              </>
            )}
          </>
        ) : null}
      </QueryState>
      <SparkEntryModal
        isOpen={isEntryOpen}
        onClose={() => setIsEntryOpen(false)}
        currency={spark?.currency ?? "USD"}
      />
    </div>
  );
}

type LifeDayForm = {
  lifeDate: string;
  type: LifeDayInput["type"];
  impact: "full" | "partial" | "expense_only";
  capacityPercent: string;
  expense: string;
  expensePurposeId: string;
  note: string;
};

export function LifeDayNewPage() {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<string>();
  const purposesQuery = useQuery({ queryKey: ["purposes"], queryFn: () => api.getPurposes() });
  const { register, handleSubmit, watch } = useForm<LifeDayForm>({
    defaultValues: {
      lifeDate: new Date().toISOString().slice(0, 10),
      type: "family_caregiving",
      impact: "full",
      capacityPercent: "50",
      expense: "",
      expensePurposeId: "",
      note: "",
    },
  });
  const impact = watch("impact");
  const mutation = useMutation({
    mutationFn: (input: LifeDayInput) => api.createLifeDay(input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["spark"] });
      await queryClient.invalidateQueries({ queryKey: ["briefing"] });
      navigate("/spark", { replace: true, state: { planUpdated: true } });
    },
  });
  const submit = handleSubmit((values) => {
    setFormError(undefined);
    const expenseMinor = values.expense ? parseMoneyInput(values.expense) : null;
    if (values.expense && (!expenseMinor || expenseMinor.startsWith("-"))) {
      setFormError("Enter an optional expense as a positive amount.");
      return;
    }
    const capacityFraction =
      values.impact === "full"
        ? 0
        : values.impact === "expense_only"
          ? 1
          : Number(values.capacityPercent) / 100;
    mutation.mutate({
      lifeDate: values.lifeDate,
      type: values.type,
      capacityFraction,
      ...(expenseMinor ? { expenseMinor } : {}),
      ...(values.expensePurposeId ? { expensePurposeId: values.expensePurposeId } : {}),
      ...(values.note ? { note: values.note } : {}),
    });
  });
  return (
    <div className="page-stack">
      <PageHeader
        eyebrow="Context, not failure"
        title="Record a life day"
        description="Family, illness, school, hockey, weather, travel, or caregiving can change work capacity. IterNest Money preserves the original plan and updates the current one."
      />
      <Card>
        <form className="form-stack" onSubmit={submit}>
          <div className="form-grid form-grid--two">
            <FormField label="Date" htmlFor="life-day-date">
              <TextInput
                id="life-day-date"
                type="date"
                {...register("lifeDate", { required: true })}
              />
            </FormField>
            <FormField label="Context" htmlFor="life-day-type">
              <SelectInput id="life-day-type" {...register("type")}>
                <option value="family_caregiving">Family / caregiving</option>
                <option value="school_hockey">School / hockey</option>
                <option value="illness">Illness</option>
                <option value="travel_weather">Travel / weather</option>
                <option value="other">Other</option>
              </SelectInput>
            </FormField>
          </div>
          <fieldset className="plain-fieldset">
            <legend>Effect on the day</legend>
            <div className="form-grid form-grid--two">
              <label className="radio-card">
                <input type="radio" value="full" {...register("impact")} />
                <span>
                  <strong>Full life day</strong>
                  <br />
                  <span className="small muted">Remove the planned Spark workday.</span>
                </span>
              </label>
              <label className="radio-card">
                <input type="radio" value="partial" {...register("impact")} />
                <span>
                  <strong>Partial capacity</strong>
                  <br />
                  <span className="small muted">Keep part of the workable day.</span>
                </span>
              </label>
              <label className="radio-card">
                <input type="radio" value="expense_only" {...register("impact")} />
                <span>
                  <strong>Expense only</strong>
                  <br />
                  <span className="small muted">Work capacity did not change.</span>
                </span>
              </label>
            </div>
          </fieldset>
          {impact === "partial" ? (
            <FormField label="Capacity still available (%)" htmlFor="life-day-capacity">
              <TextInput
                id="life-day-capacity"
                type="number"
                min={0}
                max={100}
                {...register("capacityPercent")}
              />
            </FormField>
          ) : null}
          <div className="form-grid form-grid--two">
            <FormField label="Optional confirmed expense" htmlFor="life-day-expense">
              <TextInput
                id="life-day-expense"
                inputMode="decimal"
                placeholder="0.00"
                {...register("expense")}
              />
            </FormField>
            <FormField label="Expense purpose" htmlFor="life-day-purpose">
              <SelectInput id="life-day-purpose" {...register("expensePurposeId")}>
                <option value="">Choose if there is an expense</option>
                {(purposesQuery.data?.data ?? []).map((purpose) => (
                  <option value={purpose.id} key={purpose.id}>
                    {purpose.name}
                  </option>
                ))}
              </SelectInput>
            </FormField>
          </div>
          <FormField label="Optional note" htmlFor="life-day-note">
            <textarea id="life-day-note" className="mm-input" {...register("note")} />
          </FormField>
          <Callout title="Plan updated, not behind" tone="info">
            IterNest Money will recalculate remaining workable days, Spark pressure, and the daily
            recommendation immediately after saving.
          </Callout>
          {formError ? (
            <Callout title="Check the expense" tone="warning">
              {formError}
            </Callout>
          ) : null}
          {mutation.error ? (
            <Callout title="Life day not saved" tone="urgent">
              {mutation.error.message}
            </Callout>
          ) : null}
          <div className="button-row button-row--end">
            <Link className="mm-button mm-button--quiet" to="/spark">
              Cancel
            </Link>
            <Button type="submit" isLoading={mutation.isPending}>
              Save and update plan
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
